// Viewer-side arcade state: which cabinet you're standing at, whether its
// game is open (popup or VR panel), and who else is playing which cabinet.
// Local only. The only thing ever sent to others is "X is playing / stopped"
// on this cabinet (no game state), via the world channel (gameBroadcast).

import { create } from 'zustand';

export interface ArcadeNear { id: string; gameId: string }

interface ArcadeStore {
  /** The cabinet you're at and facing (desktop / phone launch panel, VR panel). */
  near: ArcadeNear | null;
  /** The cabinet whose game is running here (popup or VR panel). */
  playing: ArcadeNear | null;
  /** You closed the launch panel at this cabinet: hidden until you walk away. */
  dismissed: string | null;
  /** cabinetId → name of someone else playing it (+ when we last heard). */
  others: Record<string, { name: string; at: number }>;
  setPlaying: (p: ArcadeNear | null) => void;
  setDismissed: (id: string | null) => void;
  setOther: (cabinetId: string, name: string | null) => void;
}

export const useArcade = create<ArcadeStore>((set) => ({
  near: null,
  playing: null,
  dismissed: null,
  others: {},
  setPlaying: (playing) => set({ playing }),
  setDismissed: (dismissed) => set({ dismissed }),
  setOther: (cabinetId, name) => set((s) => {
    const others = { ...s.others };
    if (name) others[cabinetId] = { name, at: Date.now() }; else delete others[cabinetId];
    return { others };
  }),
}));

// ---- nearest cabinet -------------------------------------------------------
// Every cabinet reports a score each frame (distance, or Infinity when you're
// not at it / not facing it); the lowest wins. State changes only when the
// winner does.

const claims = new Map<string, { score: number; gameId: string }>();

export function claimArcadeNear(id: string, gameId: string, score: number) {
  if (Number.isFinite(score)) claims.set(id, { score, gameId }); else claims.delete(id);
  let best: ArcadeNear | null = null;
  let bestScore = Infinity;
  for (const [cid, c] of claims) if (c.score < bestScore) { bestScore = c.score; best = { id: cid, gameId: c.gameId }; }
  const s = useArcade.getState();
  const cur = s.near;
  if (cur?.id === best?.id && cur?.gameId === best?.gameId) return;
  useArcade.setState({ near: best, ...(s.dismissed && s.dismissed !== best?.id ? { dismissed: null } : {}) });
}

/** "Playing" heartbeats older than this are treated as stale (the player left without saying). */
export const OTHER_TTL_MS = 25_000;
